'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { sortOptions as standardSortOptions } from './constants';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import useUserSettings from 'src/hooks/users/useUserSettings';
import useIsAdvancedSearchActive from 'src/hooks/discovery/useIsAdvancedSearchActive';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import { SortDropdown } from 'src/components/Maps/ResultsList/SortDropdown';
import type {
  SortDropdownGroup,
  SortDropdownOption,
} from 'src/components/Maps/ResultsList/SortDropdown';
import { useGetAdvancedSearchMetricsQuery } from 'src/api/redux/advancedSearches';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CLAIMS_LABEL, claimsMetricLabel, isReadyCohortVolume } from 'src/utils/encounterLabels';

export type SortOption = {
  label: string;
  order: 'asc' | 'desc';
  sort: string;
  direction?: string;
  metricId?: number;
  metricLabel?: string;
  displayLabel?: string;
};

type DropdownSortOption = SortOption & SortDropdownOption;

const toDropdownOption = (option: SortOption): DropdownSortOption => ({
  ...option,
  key: `${option.sort}-${option.order}`,
  // Inside a metric group the header names the metric: "Highest Claims".
  optionLabel:
    option.direction && option.displayLabel
      ? `${option.direction} ${option.displayLabel}`
      : undefined,
});

const ResultSortDropdown = () => {
  const { sort, order, setSort, setOrder } = useDiscoveryList();
  const { apiFilterFormat } = useFilterSets(LOCATION.DISCOVERY);
  const { settings } = useUserSettings();
  const isAdvancedSearchActive = useIsAdvancedSearchActive();
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const { showEncountersTotals } = useFlags();

  // An encounters (cohort) search sorts on the encounters metric behind the flag, so
  // the claims sort options are labeled "Encounters" to match. The `sort` key is
  // untouched — only the visible label changes. Mirrors the `isCohortReady` gate in
  // useDiscoveryQueryParams: only a READY cohort actually sorts by encounters.
  const standardOptions = useMemo((): SortOption[] => {
    const metricLabel = claimsMetricLabel(
      isReadyCohortVolume(appliedSavedVolume),
      !!showEncountersTotals
    );
    if (metricLabel === CLAIMS_LABEL) return standardSortOptions;

    return standardSortOptions.map((option) =>
      option.sort === 'claims'
        ? { ...option, label: option.label.replace(CLAIMS_LABEL, metricLabel) }
        : option
    );
  }, [
    appliedSavedVolume?.volumeType,
    appliedSavedVolume?.status,
    showEncountersTotals,
  ]);

  // Fetch metric definitions when advanced search is active
  const advancedSearchId = isAdvancedSearchActive
    ? appliedSavedVolume?.id
    : undefined;
  const { data: metricsData } = useGetAdvancedSearchMetricsQuery(
    advancedSearchId,
    { skip: !isAdvancedSearchActive || !advancedSearchId }
  );

  // Build dynamic sort options from metric display_options
  const advancedSearchSortOptions = useMemo((): SortOption[] => {
    if (!metricsData?.results?.length) return [];

    const options: SortOption[] = [];
    for (const metric of metricsData.results) {
      for (const option of metric.display_options) {
        options.push(
          {
            label: `Highest ${metric.label} ${option.label}`,
            sort: option.code,
            order: 'desc',
            direction: 'Highest',
            metricId: metric.id,
            metricLabel: metric.label,
            displayLabel: option.label,
          },
          {
            label: `Lowest ${metric.label} ${option.label}`,
            sort: option.code,
            order: 'asc',
            direction: 'Lowest',
            metricId: metric.id,
            metricLabel: metric.label,
            displayLabel: option.label,
          }
        );
      }
    }
    return options;
  }, [metricsData]);

  const handleSortChange = (option: DropdownSortOption) => {
    setSort(option.sort);
    setOrder(option.order);
  };

  const isReferrals = settings?.prospectMode === 'sending';
  const hasOpenPayments =
    !isReferrals &&
    apiFilterFormat?.some((item) => {
      return item?.filters?.some((i) => i.type === 'OPEN_PAYMENTS');
    });

  const sortGroups = useMemo((): SortDropdownGroup<DropdownSortOption>[] => {
    if (isAdvancedSearchActive) {
      return (metricsData?.results ?? []).flatMap((metric) => {
        const options = advancedSearchSortOptions
          .filter((option) => option.metricId === metric.id)
          .map(toDropdownOption);
        return options.length
          ? [{ id: metric.id.toString(), label: metric.label, options }]
          : [];
      });
    }
    return [
      {
        id: 'standard',
        label: null,
        options: standardOptions
          .filter((option) => hasOpenPayments || option.sort !== 'total_payments')
          .map(toDropdownOption),
      },
    ];
  }, [
    isAdvancedSearchActive,
    metricsData,
    advancedSearchSortOptions,
    standardOptions,
    hasOpenPayments,
  ]);

  // Reset to highest claims when OpenPayments filters are removed (standard mode only)
  const resetToHighestClaims = useCallback(() => {
    setSort('claims');
    setOrder('desc');
  }, [setSort, setOrder]);

  useEffect(() => {
    if (!isAdvancedSearchActive && !hasOpenPayments && sort === 'total_payments') {
      resetToHighestClaims();
    }
  }, [isAdvancedSearchActive, hasOpenPayments, sort, resetToHighestClaims]);

  // Set default sort when switching to/between advanced searches
  const [lastAdvancedSearchId, setLastAdvancedSearchId] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (isAdvancedSearchActive && advancedSearchSortOptions.length > 0) {
      const searchChanged = advancedSearchId !== lastAdvancedSearchId;
      if (searchChanged) {
        setLastAdvancedSearchId(advancedSearchId);
        setSort(advancedSearchSortOptions[0].sort);
        setOrder(advancedSearchSortOptions[0].order);
        return;
      }
      const currentSortValid = advancedSearchSortOptions.some(
        (opt) => opt.sort === sort && opt.order === order
      );
      if (!currentSortValid) {
        setSort(advancedSearchSortOptions[0].sort);
        setOrder(advancedSearchSortOptions[0].order);
      }
    }
  }, [isAdvancedSearchActive, advancedSearchId, lastAdvancedSearchId, advancedSearchSortOptions, sort, order, setSort, setOrder]);

  // Reset to standard default when leaving advanced search
  useEffect(() => {
    if (!isAdvancedSearchActive) {
      setLastAdvancedSearchId(undefined);
      const isStandardSort = standardSortOptions.some(
        (opt) => opt.sort === sort
      );
      if (!isStandardSort) {
        setSort('claims');
        setOrder('desc');
      }
    }
  }, [isAdvancedSearchActive, sort, setSort, setOrder]);

  return (
    <SortDropdown
      groups={sortGroups}
      selectedKey={`${sort}-${order}`}
      onSelect={handleSortChange}
    />
  );
};

export default ResultSortDropdown;
